<div class="petty-cash-container">
  <!-- Header -->
  <div class="header">
    <div class="header-content">
      <div>
        <h1>Petty Cash</h1>
        <p>Manage and track money in hand, receipts and payments in the common Petty Cash account</p>
      </div>
      <div class="header-actions">
        <button class="btn btn-receipt" (click)="openRecordReceiptModal()">
          <i class="fas fa-arrow-down mr-2"></i> Record Receipt (In)
        </button>
        <button class="btn btn-payment" (click)="openRecordPaymentModal()">
          <i class="fas fa-arrow-up mr-2"></i> Record Payment (Out)
        </button>
      </div>
    </div>
  </div>

  <!-- Notifications -->
  <div *ngIf="successMessage" class="success-message">
    <i class="fas fa-check-circle"></i>
    <span>{{ successMessage }}</span>
  </div>

  <div *ngIf="errorMessage" class="error-message">
    <i class="fas fa-exclamation-triangle"></i>
    <span>{{ errorMessage }}</span>
  </div>

  <!-- Filters Section -->
  <div class="filters-section">
    <div class="filter-header">
      <div class="filter-title-group">
        <i class="fas fa-filter text-emerald-600 mr-2"></i>
        <h2 class="filter-title">Filters</h2>
        <span *ngIf="commonBalance !== null" class="staff-balance-pill">
          Current Cash in Hand: <strong>{{ formatCurrency(commonBalance) }}</strong>
        </span>
      </div>
      <button *ngIf="hasActiveFilters()" (click)="clearFilters()" class="clear-filters-btn">
        <i class="fas fa-times mr-1"></i> Clear Filters
      </button>
    </div>

    <form [formGroup]="filterForm" (ngSubmit)="applyFilters()">
      <div class="filter-grid">
        <!-- Transaction Type -->
        <div class="filter-item">
          <label for="filterTransactionType">Transaction Flow</label>
          <select id="filterTransactionType" formControlName="transactionType" class="filter-select">
            <option *ngFor="let type of transactionTypes" [value]="type.value">{{ type.label }}</option>
          </select>
        </div>

        <!-- Head -->
        <div class="filter-item">
          <label for="filterHead">Transaction Head</label>
          <select id="filterHead" formControlName="head" class="filter-select">
            <option value="">All Heads</option>
            <optgroup label="Receipt Heads (Money In)">
              <option *ngFor="let h of receiptHeads" [value]="h.value">{{ h.label }}</option>
            </optgroup>
            <optgroup label="Payment Heads (Money Out)">
              <option *ngFor="let h of paymentHeads" [value]="h.value">{{ h.label }}</option>
            </optgroup>
          </select>
        </div>

        <!-- From Date -->
        <div class="filter-item">
          <label for="filterFromDate">From Date</label>
          <input type="date" id="filterFromDate" formControlName="fromTransactionDate" class="filter-input" [max]="maxTransactionDate">
        </div>

        <!-- To Date -->
        <div class="filter-item">
          <label for="filterToDate">To Date</label>
          <input type="date" id="filterToDate" formControlName="toTransactionDate" class="filter-input" [max]="maxTransactionDate">
        </div>

        <!-- Apply Button -->
        <div class="filter-item filter-button-wrapper">
          <button type="submit" class="btn btn-apply-filter">
            <i class="fas fa-search mr-1"></i> Apply
          </button>
        </div>
      </div>
    </form>
  </div>

  <!-- Summary Cards (shown when staff member is selected and summary is returned) -->
  <div *ngIf="periodSummary" class="summary-cards">
    <div class="summary-card card-opening">
      <div class="card-icon"><i class="fas fa-door-open"></i></div>
      <div class="card-details">
        <span class="card-title">Opening Balance</span>
        <span class="card-value">{{ formatCurrency(periodSummary.openingBalance) }}</span>
        <span class="card-sub">Start of filtered period</span>
      </div>
    </div>

    <div class="summary-card card-receipt">
      <div class="card-icon"><i class="fas fa-hand-holding-usd"></i></div>
      <div class="card-details">
        <span class="card-title">Total Receipts (In)</span>
        <span class="card-value text-emerald-600">+{{ formatCurrency(periodSummary.totalReceipt) }}</span>
        <span class="card-sub">Money added to petty cash</span>
      </div>
    </div>

    <div class="summary-card card-payment">
      <div class="card-icon"><i class="fas fa-money-bill-wave"></i></div>
      <div class="card-details">
        <span class="card-title">Total Payments (Out)</span>
        <span class="card-value text-rose-600">-{{ formatCurrency(periodSummary.totalPayment) }}</span>
        <span class="card-sub">Expenses &amp; company transfers</span>
      </div>
    </div>

    <div class="summary-card card-closing">
      <div class="card-icon"><i class="fas fa-wallet"></i></div>
      <div class="card-details">
        <span class="card-title">Closing Balance</span>
        <span class="card-value font-bold" [ngClass]="periodSummary.closingBalance >= 0 ? 'text-emerald-700' : 'text-rose-700'">
          {{ formatCurrency(periodSummary.closingBalance) }}
        </span>
        <span class="card-sub">Cash in hand at end of period</span>
      </div>
    </div>
  </div>

  <!-- Ledger Table Section -->
  <div class="table-section">
    <div class="table-header">
      <div class="flex items-center gap-3">
        <h3 class="text-lg font-bold text-gray-800">Petty Cash Transactions</h3>
        <span class="badge badge-count">{{ totalCount }} total entries</span>
      </div>
    </div>

    <!-- Loading State -->
    <div *ngIf="loading" class="loading-state">
      <div class="spinner"></div>
      <p>Loading petty cash ledger entries...</p>
    </div>

    <!-- Empty State -->
    <div *ngIf="!loading && ledgerEntries.length === 0" class="empty-state">
      <i class="fas fa-cash-register text-4xl text-gray-300 mb-3"></i>
      <h4>No Petty Cash Entries Found</h4>
      <p>There are no transactions matching the selected criteria.</p>
    </div>

    <!-- Table -->
    <div *ngIf="!loading && ledgerEntries.length > 0" class="table-responsive">
      <table class="ledger-table">
        <thead>
          <tr>
            <th>Date</th>
            <th>Type</th>
            <th>Head</th>
            <th>Description</th>
            <th class="text-right">Receipt (In)</th>
            <th class="text-right">Payment (Out)</th>
            <th class="text-right">Closing Balance</th>
            <th>Reference</th>
            <th class="text-center">Action</th>
          </tr>
        </thead>
        <tbody>
          <tr *ngFor="let entry of ledgerEntries" [class.cancelled-row]="entry.isCancelled">
            <!-- Date -->
            <td class="font-medium whitespace-nowrap">{{ formatDate(entry.transactionDate) }}</td>

            <!-- Entry Type -->
            <td>
              <span class="entry-badge" [ngClass]="{
                'badge-receipt': entry.entryType === 'Receipt',
                'badge-payment': entry.entryType === 'Payment',
                'badge-adjustment': entry.entryType === 'Adjustment'
              }">
                <i class="fas" [ngClass]="{
                  'fa-arrow-down': entry.entryType === 'Receipt',
                  'fa-arrow-up': entry.entryType === 'Payment',
                  'fa-sync-alt': entry.entryType === 'Adjustment'
                }"></i>
                {{ entry.entryType }}
              </span>
            </td>

            <!-- Head -->
            <td>
              <span class="head-badge">{{ entry.head }}</span>
            </td>

            <!-- Description -->
            <td class="desc-cell" [title]="entry.description || ''">
              {{ entry.description || '—' }}
            </td>

            <!-- Receipt Amount (In) -->
            <td class="amount text-right receipt-val">
              <span *ngIf="entry.receiptAmount > 0" class="text-emerald-600 font-semibold">
                +{{ formatCurrency(entry.receiptAmount) }}
              </span>
              <span *ngIf="entry.receiptAmount <= 0" class="text-gray-400">—</span>
            </td>

            <!-- Payment Amount (Out) -->
            <td class="amount text-right payment-val">
              <span *ngIf="entry.paymentAmount > 0" class="text-rose-600 font-semibold">
                -{{ formatCurrency(entry.paymentAmount) }}
              </span>
              <span *ngIf="entry.paymentAmount <= 0" class="text-gray-400">—</span>
            </td>

            <!-- Closing Balance -->
            <td class="amount text-right balance-val font-bold">
              {{ formatCurrency(entry.closingBalance) }}
            </td>

            <!-- Reference -->
            <td class="reference-details">
              <div *ngIf="entry.referenceId" class="ref-info">
                <span class="ref-type-badge" [class.ref-cancellation]="entry.referenceType === 'Petty Cash Cancellation'">
                  {{ entry.referenceType || 'Ref' }}
                </span>
                <span class="ref-id" [title]="entry.referenceId">{{ entry.referenceId }}</span>
              </div>
              <span *ngIf="!entry.referenceId" class="text-gray-400">—</span>
            </td>

            <!-- Actions -->
            <td class="action-cell text-center">
              <button *ngIf="!entry.isCancelled && !isAutomaticEntry(entry)"
                      (click)="openCancelModal(entry)"
                      class="btn-cancel-action"
                      title="Cancel transaction">
                <i class="fas fa-ban"></i> Cancel
              </button>
              <span *ngIf="!entry.isCancelled && isAutomaticEntry(entry)"
                    class="auto-tag"
                    title="Automatic transaction created by external module. Cannot be cancelled directly from Petty Cash.">
                <i class="fas fa-lock"></i> Auto
              </span>
              <span *ngIf="entry.isCancelled" class="cancelled-tag">Cancelled</span>
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <!-- Pagination -->
    <div *ngIf="!loading && totalCount > 0" class="pagination-wrapper">
      <app-pagination
        [page]="currentPage"
        [totalPages]="totalPages"
        [pageSize]="pageSize"
        (pageChange)="onPageChange($event)"
        (pageSizeChange)="onPageSizeChange($event)">
      </app-pagination>
    </div>
  </div>

  <!-- Record Receipt Modal -->
  <div *ngIf="showReceiptModal" class="modal-backdrop" (click)="closeReceiptModal()">
    <div class="modal-card" (click)="$event.stopPropagation()">
      <div class="modal-header modal-header-receipt">
        <div class="flex items-center gap-2">
          <i class="fas fa-hand-holding-usd text-2xl"></i>
          <h3 class="text-xl font-bold">Record Petty Cash Receipt (Payment In)</h3>
        </div>
        <button class="modal-close-btn" (click)="closeReceiptModal()">&times;</button>
      </div>

      <form [formGroup]="receiptForm" (ngSubmit)="submitReceipt()">
        <div class="modal-body">
          <div *ngIf="errorMessage" class="error-message mb-4">
            <i class="fas fa-exclamation-triangle"></i>
            <span>{{ errorMessage }}</span>
          </div>

          <!-- Receipt Head -->
          <div class="form-group mb-4">
            <label for="receiptHead" class="form-label">Receipt Head <span class="text-rose-500">*</span></label>
            <select id="receiptHead" formControlName="head" class="form-input">
              <option *ngFor="let h of receiptHeads" [value]="h.value">{{ h.label }}</option>
            </select>
            <span class="text-xs text-gray-500 mt-1 block">
              <i class="fas fa-info-circle"></i> "Agent Payment" requires a reference ID (e.g. receipt or payment ref).
            </span>
          </div>

          <!-- Amount & Date -->
          <div class="form-row mb-4">
            <div class="form-group flex-1">
              <label for="receiptAmount" class="form-label">Receipt Amount (₹) <span class="text-rose-500">*</span></label>
              <input type="number" id="receiptAmount" formControlName="receiptAmount" step="0.01" min="0.01" placeholder="0.00" class="form-input">
            </div>

            <div class="form-group flex-1">
              <label for="receiptDate" class="form-label">Transaction Date <span class="text-rose-500">*</span></label>
              <input type="date" id="receiptDate" formControlName="transactionDate" class="form-input" [min]="minTransactionDate" [max]="maxTransactionDate">
            </div>
          </div>

          <!-- Reference ID -->
          <div class="form-group mb-4">
            <label for="receiptRefId" class="form-label">
              Reference ID <span *ngIf="isReceiptRefRequired()" class="text-rose-500">* (Required for Agent Payment)</span>
            </label>
            <input type="text" id="receiptRefId" formControlName="referenceId" placeholder="e.g. AGT-PAY-100234" class="form-input">
          </div>

          <!-- Description -->
          <div class="form-group mb-2">
            <label for="receiptDesc" class="form-label">Description / Remarks (Optional)</label>
            <textarea id="receiptDesc" formControlName="description" rows="2" placeholder="e.g. Cash collected from agent XYZ" class="form-input"></textarea>
          </div>
        </div>

        <div class="modal-footer">
          <button type="button" class="btn btn-secondary" (click)="closeReceiptModal()" [disabled]="submittingReceipt">Cancel</button>
          <button type="submit" class="btn btn-receipt" [disabled]="receiptForm.invalid || submittingReceipt">
            <span *ngIf="submittingReceipt"><i class="fas fa-spinner fa-spin mr-1"></i> Saving...</span>
            <span *ngIf="!submittingReceipt"><i class="fas fa-check mr-1"></i> Save Receipt</span>
          </button>
        </div>
      </form>
    </div>
  </div>

  <!-- Record Payment Modal -->
  <div *ngIf="showPaymentModal" class="modal-backdrop" (click)="closePaymentModal()">
    <div class="modal-card" (click)="$event.stopPropagation()">
      <div class="modal-header modal-header-payment">
        <div class="flex items-center gap-2">
          <i class="fas fa-money-bill-wave text-2xl"></i>
          <h3 class="text-xl font-bold">Record Petty Cash Payment (Payment Out)</h3>
        </div>
        <button class="modal-close-btn" (click)="closePaymentModal()">&times;</button>
      </div>

      <form [formGroup]="paymentForm" (ngSubmit)="submitPayment()">
        <div class="modal-body">
          <div *ngIf="errorMessage" class="error-message mb-4">
            <i class="fas fa-exclamation-triangle"></i>
            <span>{{ errorMessage }}</span>
          </div>

          <!-- Payment Head -->
          <div class="form-group mb-4">
            <label for="paymentHead" class="form-label">Payment Head <span class="text-rose-500">*</span></label>
            <select id="paymentHead" formControlName="head" class="form-input">
              <option *ngFor="let h of paymentHeads" [value]="h.value">{{ h.label }}</option>
            </select>
            <span class="text-xs text-gray-500 mt-1 block">
              <i class="fas fa-info-circle"></i> "Company Transfer" requires bank deposit / reference ID.
            </span>
          </div>

          <!-- Amount & Date -->
          <div class="form-row mb-4">
            <div class="form-group flex-1">
              <label for="paymentAmount" class="form-label">Payment Amount (₹) <span class="text-rose-500">*</span></label>
              <input type="number" id="paymentAmount" formControlName="paymentAmount" step="0.01" min="0.01" placeholder="0.00" class="form-input">
            </div>

            <div class="form-group flex-1">
              <label for="paymentDate" class="form-label">Transaction Date <span class="text-rose-500">*</span></label>
              <input type="date" id="paymentDate" formControlName="transactionDate" class="form-input" [min]="minTransactionDate" [max]="maxTransactionDate">
            </div>
          </div>

          <!-- Reference ID -->
          <div class="form-group mb-4">
            <label for="paymentRefId" class="form-label">
              Reference ID <span *ngIf="isPaymentRefRequired()" class="text-rose-500">* (Required for Company Transfer)</span>
            </label>
            <input type="text" id="paymentRefId" formControlName="referenceId" placeholder="e.g. BANK-DEP-994821" class="form-input">
          </div>

          <!-- Description -->
          <div class="form-group mb-2">
            <label for="paymentDesc" class="form-label">Description / Remarks (Optional)</label>
            <textarea id="paymentDesc" formControlName="description" rows="2" placeholder="e.g. Travel petrol expense for route inspection" class="form-input"></textarea>
          </div>
        </div>

        <div class="modal-footer">
          <button type="button" class="btn btn-secondary" (click)="closePaymentModal()" [disabled]="submittingPayment">Cancel</button>
          <button type="submit" class="btn btn-payment" [disabled]="paymentForm.invalid || submittingPayment">
            <span *ngIf="submittingPayment"><i class="fas fa-spinner fa-spin mr-1"></i> Saving...</span>
            <span *ngIf="!submittingPayment"><i class="fas fa-check mr-1"></i> Save Payment</span>
          </button>
        </div>
      </form>
    </div>
  </div>

  <!-- Cancel Confirmation Modal -->
  <div *ngIf="showCancelModal && selectedEntryToCancel" class="modal-backdrop" (click)="closeCancelModal()">
    <div class="modal-card modal-card-sm" (click)="$event.stopPropagation()">
      <div class="modal-header modal-header-cancel">
        <div class="flex items-center gap-2">
          <i class="fas fa-exclamation-triangle text-amber-500 text-xl"></i>
          <h3 class="text-xl font-bold">Cancel Petty Cash Entry</h3>
        </div>
        <button class="modal-close-btn" (click)="closeCancelModal()">&times;</button>
      </div>

      <div class="modal-body">
        <p class="text-sm text-gray-700 mb-4">
          Are you sure you want to cancel this petty cash transaction? A reversal adjustment entry will be automatically created to restore the cash balance.
        </p>

        <div class="cancel-summary-box mb-4">
          <div class="flex justify-between py-1 text-sm">
            <span class="text-gray-500">Staff:</span>
            <span class="font-semibold">{{ selectedEntryToCancel.staffName }}</span>
          </div>
          <div class="flex justify-between py-1 text-sm">
            <span class="text-gray-500">Head:</span>
            <span class="font-semibold">{{ selectedEntryToCancel.head }}</span>
          </div>
          <div class="flex justify-between py-1 text-sm">
            <span class="text-gray-500">Amount:</span>
            <span class="font-bold">
              {{ formatCurrency(selectedEntryToCancel.receiptAmount || selectedEntryToCancel.paymentAmount) }}
            </span>
          </div>
          <div class="flex justify-between py-1 text-sm">
            <span class="text-gray-500">Date:</span>
            <span>{{ formatDate(selectedEntryToCancel.transactionDate) }}</span>
          </div>
        </div>

        <div class="form-group">
          <label for="cancelReason" class="form-label">Cancellation Reason (Optional)</label>
          <textarea id="cancelReason" [(ngModel)]="cancelReason" rows="2" placeholder="e.g. Incorrect amount entered" class="form-input"></textarea>
        </div>
      </div>

      <div class="modal-footer">
        <button type="button" class="btn btn-secondary" (click)="closeCancelModal()" [disabled]="cancellingEntry">Keep Entry</button>
        <button type="button" class="btn btn-danger" (click)="confirmCancelEntry()" [disabled]="cancellingEntry">
          <span *ngIf="cancellingEntry"><i class="fas fa-spinner fa-spin mr-1"></i> Cancelling...</span>
          <span *ngIf="!cancellingEntry"><i class="fas fa-ban mr-1"></i> Confirm Cancellation</span>
        </button>
      </div>
    </div>
  </div>
</div>
